import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../components/ui/Toast';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/Textarea';
import Icon from '../../../components/AppIcon';
import notificationService from '../../../services/notificationService';

/**
 * Send a notification to people in this conference.
 *
 * The previous version was reported as uncomfortable to use. It was worse than
 * that — it didn't work:
 *
 *   - Three of its dropdowns (template, notification type, recipients) were
 *     `<Select>` with `<option>` children. `Select` renders from an `options`
 *     prop and ignores children, so all three were permanently empty. Since
 *     "Notification Type" was marked required, the form could not be completed.
 *   - The Subject field passed `onChange={(value) => …}` to `Input`, which is a
 *     wrapper around a native input and hands its listener an *event*. So the
 *     subject was being set to a SyntheticEvent rather than the typed text.
 *   - It asked you to write the message before choosing who it was for, which
 *     is backwards: who you're addressing changes what you write.
 *
 * Rebuilt around the order the task actually happens in — pick the people, see
 * exactly who that is, then write to them — and with the ability to send to
 * named individuals, which the group-only recipient list never allowed.
 */
const NotificationForm = ({ conferenceId, templates, onSuccess }) => {
  const { t } = useTranslation('notifications');
  const { success: toastSuccess, error: toastError } = useToast();

  const [audience, setAudience] = useState('all_reviewers');
  const [filter, setFilter] = useState({});
  const [individuals, setIndividuals] = useState([]);   // chosen user ids
  const [search, setSearch] = useState('');

  const [templateId, setTemplateId] = useState('');
  const [notificationType, setNotificationType] = useState('custom');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const [papers, setPapers] = useState([]);
  const [directory, setDirectory] = useState([]);       // everyone in this conference
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const k = (key, fallback, opts) => t(`notificationForm.${key}`, { defaultValue: fallback, ...(opts || {}) });

  useEffect(() => {
    notificationService?.getPapers(conferenceId)?.then(setPapers)?.catch(() => setPapers([]));
    // Everyone with any role here, so individuals can be picked by name.
    notificationService?.getRecipients(conferenceId, 'everyone', {})
      ?.then((r) => setDirectory(r || []))
      ?.catch(() => setDirectory([]));
  }, [conferenceId]);

  // Resolve the audience into an actual list of people.
  useEffect(() => {
    let cancelled = false;
    if (audience === 'individuals') {
      setRecipients(directory.filter((p) => individuals.includes(p?.id)));
      return undefined;
    }
    setLoadingRecipients(true);
    notificationService
      ?.getRecipients(conferenceId, audience, filter)
      ?.then((r) => { if (!cancelled) setRecipients(r || []); })
      ?.catch(() => { if (!cancelled) setRecipients([]); })
      ?.finally(() => { if (!cancelled) setLoadingRecipients(false); });
    return () => { cancelled = true; };
  }, [conferenceId, audience, filter, individuals, directory]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = templates?.find((x) => x?.id === id);
    if (tpl) {
      setNotificationType(tpl?.notificationType || 'custom');
      setSubject(tpl?.subject || '');
      setMessage(tpl?.message || '');
    }
  };

  const audienceOptions = [
    { value: 'all_reviewers', label: k('audience.allReviewers', 'All reviewers') },
    { value: 'all_authors', label: k('audience.allAuthors', 'All authors') },
    { value: 'role_based', label: k('audience.byRole', 'Everyone with a role') },
    { value: 'paper_reviewers', label: k('audience.paperReviewers', 'Reviewers of one paper') },
    { value: 'paper_authors', label: k('audience.paperAuthors', 'Author of one paper') },
    { value: 'individuals', label: k('audience.individuals', 'Specific people') },
  ];

  const typeOptions = [
    { value: 'custom', label: k('type.custom', 'Custom message') },
    { value: 'assignment', label: k('type.assignment', 'Reviewer assignment') },
    { value: 'decision', label: k('type.decision', 'Paper decision') },
    { value: 'status_update', label: k('type.statusUpdate', 'Status update') },
    { value: 'deadline_reminder', label: k('type.deadline', 'Deadline reminder') },
  ];

  const roleOptions = [
    { value: 'chair', label: k('role.chair', 'Chairs') },
    { value: 'organizer', label: k('role.organizer', 'Organizers') },
    { value: 'reviewer', label: k('role.reviewer', 'Reviewers') },
    { value: 'author', label: k('role.author', 'Authors') },
  ];

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter((p) =>
      [p?.firstName, p?.lastName, p?.email, p?.institution]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [directory, search]);

  const toggleIndividual = (id) =>
    setIndividuals((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const needsPaper = audience === 'paper_reviewers' || audience === 'paper_authors';
  const needsRole = audience === 'role_based';

  const validate = () => {
    const e = {};
    if (!subject.trim()) e.subject = k('errors.subject', 'A subject is required.');
    if (!message.trim()) e.message = k('errors.message', 'A message is required.');
    if (needsPaper && !filter?.submissionId) e.filter = k('errors.paper', 'Choose which paper.');
    if (needsRole && !filter?.role) e.filter = k('errors.role', 'Choose which role.');
    if (recipients.length === 0) e.recipients = k('errors.recipients', 'This would go to nobody.');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const send = async (immediately) => {
    if (!validate()) return;
    try {
      setSending(true);
      await notificationService?.createNotification({
        conferenceId,
        templateId: templateId || null,
        notificationType,
        subject,
        message,
        recipientType: audience,
        recipientFilter: audience === 'individuals' ? { userIds: individuals } : filter,
        sendImmediately: immediately,
      });
      toastSuccess(
        immediately
          ? k('sent', 'Sent to {{count}} people.', { count: recipients.length })
          : k('saved', 'Saved as a draft.')
      );
      setSubject(''); setMessage(''); setTemplateId('');
      setIndividuals([]); setSearch('');
      onSuccess?.();
    } catch (err) {
      toastError(err?.message || k('errors.failed', 'Could not send the notification.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-card border border-border">
      <div className="px-5 py-3 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">{k('title', 'Send a notification')}</h2>
      </div>

      <div className="p-5 space-y-6">
        {/* ---- 1. Who ------------------------------------------------------
            Deliberately first. Who you're writing to changes what you write,
            and it's the only part of this form that can be wrong in a way you
            can't see afterwards. */}
        <section className="space-y-3">
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-semibold text-foreground">{k('step.who', 'Who is this for?')}</h3>
          </div>

          <Select
            label={k('audienceLabel', 'Audience')}
            value={audience}
            onChange={(v) => { setAudience(v); setFilter({}); }}
            options={audienceOptions}
          />

          {needsPaper && (
            <Select
              label={k('paperLabel', 'Which paper')}
              value={filter?.submissionId || ''}
              onChange={(v) => setFilter({ submissionId: v })}
              error={errors.filter}
              searchable
              placeholder={k('paperPlaceholder', 'Choose a paper')}
              options={(papers || []).map((p) => ({
                value: p?.id,
                label: `${p?.title}${p?.authorName ? ` — ${p.authorName}` : ''}`,
              }))}
            />
          )}

          {needsRole && (
            <Select
              label={k('roleLabel', 'Which role')}
              value={filter?.role || ''}
              onChange={(v) => setFilter({ role: v })}
              error={errors.filter}
              placeholder={k('rolePlaceholder', 'Choose a role')}
              options={roleOptions}
            />
          )}

          {audience === 'individuals' && (
            <div className="border border-border rounded-md overflow-hidden">
              <div className="p-2 border-b border-border">
                <Input
                  type="search"
                  placeholder={k('searchPeople', 'Search by name, email or institution')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-border">
                {matches.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-4 text-center">
                    {k('noPeople', 'Nobody matches that.')}
                  </p>
                ) : (
                  matches.map((p) => {
                    const on = individuals.includes(p?.id);
                    return (
                      <button
                        key={p?.id}
                        type="button"
                        onClick={() => toggleIndividual(p?.id)}
                        aria-pressed={on}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-start transition-colors ${
                          on ? 'bg-primary/5' : 'hover:bg-muted/50'
                        }`}
                      >
                        <Icon
                          name={on ? 'CheckSquare' : 'Square'}
                          size={15}
                          className={on ? 'text-primary flex-shrink-0' : 'text-muted-foreground flex-shrink-0'}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground truncate">
                            {[p?.firstName, p?.lastName].filter(Boolean).join(' ') || p?.email}
                          </span>
                          <span className="block text-[11px] text-muted-foreground truncate">
                            {[p?.email, p?.institution].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Who this actually reaches, by name where the list is short enough
              to name. A bare count can't tell you that you picked the wrong
              paper — seeing "Hakim Ouali" can. */}
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5">
            {loadingRecipients ? (
              <span className="text-xs text-muted-foreground flex items-center gap-2">
                <Icon name="Loader2" size={13} className="animate-spin" />
                {k('resolving', 'Working out who that is…')}
              </span>
            ) : recipients.length === 0 ? (
              <span className="text-xs text-error flex items-center gap-1.5">
                <Icon name="AlertCircle" size={13} />
                {k('nobody', 'This would go to nobody.')}
              </span>
            ) : (
              <>
                <p className="text-xs font-medium text-foreground">
                  {k('goesTo', 'Goes to {{count}} people', { count: recipients.length })}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                  {recipients.slice(0, 8)
                    .map((p) => [p?.firstName, p?.lastName].filter(Boolean).join(' ') || p?.email)
                    .join(', ')}
                  {recipients.length > 8 &&
                    ` ${k('andMore', 'and {{count}} more', { count: recipients.length - 8 })}`}
                </p>
              </>
            )}
          </div>
        </section>

        {/* ---- 2. What ---------------------------------------------------- */}
        <section className="space-y-3 pt-2 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground pt-3">{k('step.what', 'What do you want to say?')}</h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Select
              label={k('templateLabel', 'Start from a template')}
              value={templateId}
              onChange={applyTemplate}
              clearable
              placeholder={k('templatePlaceholder', 'Write from scratch')}
              options={(templates || []).map((x) => ({ value: x?.id, label: x?.name }))}
            />
            <Select
              label={k('typeLabel', 'Type')}
              value={notificationType}
              onChange={setNotificationType}
              options={typeOptions}
            />
          </div>

          <Input
            label={k('subjectLabel', 'Subject')}
            required
            placeholder={k('subjectPlaceholder', 'What is this about?')}
            value={subject}
            /* Input wraps a native <input>, so this handler receives an event.
               The old code read `(value) => …` here and stored the event. */
            onChange={(e) => setSubject(e.target.value)}
            error={errors.subject}
          />

          <Textarea
            label={k('messageLabel', 'Message')}
            required
            rows={8}
            maxLength={5000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={k('messagePlaceholder', 'Write your message…')}
            error={errors.message}
          />
        </section>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={() => send(false)} disabled={sending} iconName="Save" iconPosition="left">
            {k('saveDraft', 'Save as draft')}
          </Button>
          <Button
            onClick={() => send(true)}
            loading={sending}
            disabled={sending || recipients.length === 0}
            iconName="Send"
            iconPosition="left"
          >
            {recipients.length > 0
              ? k('sendToCount', 'Send to {{count}}', { count: recipients.length })
              : k('send', 'Send')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationForm;
